import React from 'react';
import { UtensilsCrossed, Star, Clock } from 'lucide-react';

/**
 * CoverPhotoPreview — tiny "as seen in search results" mock of the
 * RestaurantCard rendered on /restaurants. Restaurant owners can see in
 * real-time how their Main Restaurant Photo will look to customers — the
 * uploaded image, an Open dot, the restaurant name + cuisine, and a
 * sample rating star — before they ever publish.
 *
 * This is purely visual feedback. No data is sent or stored.
 *
 * Props:
 *   - coverUrl:  the current `cover_url` from the form (may be empty)
 *   - name:      the current restaurant name (falls back to "Your Restaurant")
 *   - cuisine:   optional cuisine string to render under the name
 *   - testid:    optional data-testid prefix for end-to-end tests
 */
const CoverPhotoPreview = ({ coverUrl, name, cuisine, testid = 'cover-preview-card' }) => {
  const safeName = (name || '').trim() || 'Your Restaurant';
  return (
    <div className="mt-3" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/40 font-bold mb-2">
        Preview · How customers see it in search
      </p>
      <div className="w-full max-w-[280px] rounded-2xl overflow-hidden border border-white/10 bg-zinc-950 shadow-lg">
        {/* 16:9 photo area, same gradient overlay as the real card. */}
        <div className="relative aspect-[16/9] bg-zinc-900">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt="Your restaurant photo preview"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`${testid}-img`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
              <UtensilsCrossed className="w-9 h-9 text-white/10" />
            </div>
          )}
          {/* Bottom-up dark fade so the white text on the photo always reads. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          {/* Sample Open badge top-left to mirror the real directory card. */}
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] font-bold rounded-full bg-emerald-500/90 text-black">
            <span className="w-1.5 h-1.5 rounded-full bg-black" />
            Open
          </span>
        </div>
        {/* Card footer — matches the layout of RestaurantCard.jsx. */}
        <div className="px-3 py-2.5 bg-black">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-[13px] font-bold text-white leading-tight truncate" data-testid={`${testid}-name`}>
              {safeName}
            </h4>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3 h-3 fill-gold-400 text-gold-400" />
              <span className="text-[11px] font-bold text-white">4.8</span>
            </div>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/55">
            <Clock className="w-3 h-3" /> 20–35 min
            {cuisine && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="truncate">{cuisine}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverPhotoPreview;
